import type { MonsterDef } from '../../sim/registry';
import { monsterStats } from '../balance';
import { circle, cone, rect, ring } from '../classes/kit';
import { mAreas, mCharge, mCombo, mLeap, mMelee, mRoar, mShoot, mStaggerCheck, mSupport } from './kit';

/**
 * Hollowroot Crypt (Act I dungeon, levels 7–9). Reuses `grubling` and
 * `grubling_brute` from Emberwild. Boss script: src/content/bosses/story.ts.
 */

const HEX = 0xa060ff;
const MEND = 0x7bd66b;
const SHARD = 0x8fe4ff;

export const CRYPT_MONSTERS: MonsterDef[] = [
  {
    id: 'grubling_shaman', name: 'Grubling Shaman', grade: 'normal', model: 'mob_grubling', scale: 1.05, radius: 0.45, tint: 0x8a5ad0, creature: 'Goblinoid',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.75, atk: 0.9 }),
    skills: [
      mShoot('shaman_hex_bolt', 'Hex Bolt', { coef: 1.2, speed: 13, range: 14, windup: 0.6, cd: 2.4, anim: 'Cast', hue: HEX }),
      // Shields every ally around the shaman (the chieftain included).
      mSupport('shaman_bone_ward', 'Bone Ward', { radius: 7, shieldPct: 0.4, shieldDuration: 6, cd: 12, range: [0, 16], hue: HEX }),
      // Plants a ticking mending field: the "totem" heals allies standing in it.
      mSupport('shaman_mending_totem', 'Mending Totem', { radius: 5, healPct: 0.12, duration: 5, tick: 1, cd: 16, range: [0, 16], hue: MEND, vfx: 'pillar' }),
    ],
    brain: 'caster', aggroRange: 12, leash: 24, xp: 28, loot: 'grubling', weakness: 'fire',
  },
  {
    id: 'crypt_skeleton', name: 'Crypt Skeleton', grade: 'normal', model: 'mob_skeleton', scale: 1, radius: 0.45, creature: 'Undead',
    stats: (l) => monsterStats(l, 'normal', { hp: 1.1, speed: 0.95 }),
    skills: [
      mMelee('cskel_hack', 'Rusted Hack', { shape: cone(2.4, 80), coef: 1.2, windup: 0.5, cd: 2.2 }),
      mCombo('cskel_flurry', 'Bone Flurry', {
        hits: [
          { at: 0.45, shape: cone(2.4, 90), coef: 0.7 },
          { at: 0.8, shape: cone(2.6, 90), coef: 0.9, step: 0.6, reaction: { kind: 'knockback', power: 1.2 } },
        ],
        cd: 7,
      }),
    ],
    brain: 'melee', aggroRange: 9, leash: 22, xp: 26, loot: 'normal', weakness: 'holy',
  },
  {
    id: 'grubling_chieftain', name: 'Grubling Chieftain', grade: 'boss', model: 'boss_chieftain', scale: 1, radius: 1.3, creature: 'Goblinoid',
    title: 'Warlord of the Warren', stagger: 420, hpBars: 24, weakness: 'fire',
    stats: (l) => monsterStats(l, 'boss', { hp: 0.9, speed: 1.05 }),
    skills: [
      mAreas('chief_club_slam', 'Club Slam', { steps: [{ shape: circle(2.8, 2.6), delay: 0.75, coef: 2.6 }], cd: 3.5, range: [0, 4.5], hue: SHARD }),
      mAreas('chief_twin_smash', 'Twin Smash', {
        steps: [
          { shape: cone(4.4, 80, -40), delay: 0.65, coef: 1.7, reaction: { kind: 'knockback', power: 1.5 } },
          { shape: cone(4.4, 80, 40), delay: 1.1, coef: 1.7 },
        ],
        cd: 6, range: [0, 4], anim: 'Attack2', hue: SHARD,
      }),
      mAreas('chief_brutal_swipe', 'Brutal Swipe', {
        steps: [{ shape: cone(5.5, 150), delay: 0.85, coef: 2.2, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3.5 } }],
        cd: 7, range: [0, 5], anim: 'Attack',
      }),
      mLeap('chief_crushing_leap', 'Crushing Leap', { radius: 4.2, coef: 3, maxDistance: 14, land: 1.1, cd: 10, range: [4, 14], hue: SHARD }),
      // Counterable: blue glow over the back of the wind-up; a counter stops the rush.
      mCharge('chief_shardbound_charge', 'Shardbound Charge', { distance: 13, width: 2.8, windup: 1.2, coef: 3.6, count: 3, counterable: true, cd: 14, range: [3, 13], hue: SHARD }),
      // Summon: the boss script spawns grublings when the roar lands (0.8 s).
      mRoar('chief_rally', 'Rally the Warren', {
        announce: 'The Chieftain calls the warren to battle!', duration: 1.6, cd: 30,
        nova: { shape: circle(5), delay: 0.8, coef: 0.8, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3 } },
      }),
      mAreas('chief_shard_fissure', 'Shard Fissure', {
        steps: [
          { shape: rect(14, 2.4), delay: 0.8, coef: 2.2, reaction: { kind: 'launch', power: 0.5, duration: 0.9 } },
          { shape: rect(14, 2.4, 0, -35), delay: 1.1, coef: 2.2, reaction: { kind: 'launch', power: 0.5, duration: 0.9 } },
          { shape: rect(14, 2.4, 0, 35), delay: 1.4, coef: 2.2, reaction: { kind: 'launch', power: 0.5, duration: 0.9 } },
        ],
        cd: 11, range: [0, 12], hue: SHARD,
      }),
      // In-out: hug him for the donut, then step out of the inner slam.
      mAreas('chief_warren_quake', 'Warren Quake', {
        steps: [
          { shape: ring(3.6, 11), delay: 1.1, coef: 2.4 },
          { shape: circle(3.6), delay: 2, coef: 2.6 },
        ],
        cd: 16, range: [0, 8],
      }),
      // Stagger check at 50%: empty the stagger bar within 9 s or the arena is wiped.
      mStaggerCheck('chief_shard_fury', "Shard's Fury", {
        channel: 9, radius: 40, coef: 150, hue: SHARD,
        text: "The Chieftain draws on the shard's fury! Break his stance!",
      }),
      mRoar('chief_frenzy_roar', 'Frenzied Bellow', {
        announce: 'The Chieftain flies into a frenzy!', announceStyle: 'warning', duration: 1.8, cd: 60,
        nova: { shape: circle(6), delay: 0.9, coef: 1.2, telegraph: 'yellow', reaction: { kind: 'knockback', power: 4 } },
      }),
    ],
    brain: 'boss', aggroRange: 16, leash: 60, xp: 1200, loot: 'story_boss',
  },
];
